/* eslint-disable max-len */
import './NetBenefitEstimator.scss';

import {
  Table,
  Tbody,
  Td,
  Tfoot,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Loader } from 'simple-react-loader';

import * as actions from '../../actions';
import {
  BACKGROUND_HOVER,
  BACKGROUND_TABLE_HEADER,
  NEUTRAL,
} from '../../constants/colors';
import DefaultLayout from '../../layouts/DefaultLayout/DefaultLayout';
import NetBenefitEstimatorService from '../../Services/NetBenefitEstimator.service';
import Navigation from '../../shared/Navigation/Navigation';
import SelectionInputs from '../../shared/SelectionInputs/SelectionInputs';
import { getMonthName } from '../../utils';

const NetBenefitEstimator = () => {
  const { t } = useTranslation();

  const [estimatedData, setEstimatedData] = useState(null);
  const [loader, setLoader] = useState(false);
  const dispatch = useDispatch();

  const globalState = {
    selectionData: useSelector(state => state.calculator.selectionData),
  };

  const localActions = {
    calculatePremiums: (param, errorCb) => dispatch(
      actions.calculator.calculatePremiums(param, errorCb),
    ),
  };

  const formatterCurrency = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  const formatterWithoutCurrency = new Intl.NumberFormat('en-US');

  const errorCB = error => {
    toast.error(`${error}`, {
      position: toast.POSITION.TOP_RIGHT,
    });
  };

  const fetchEstimatedData = async () => {
    setLoader(true);

    const params = {
      year: globalState.selectionData.selectedYear,
      coveredProduction: globalState.selectionData.coveredProduction,
      coveragePercentage: globalState.selectionData.coveragePercentage,
      tier1Level: globalState.selectionData.coverageLevel,
      tier2Level: globalState.selectionData.tier2CoverageLevel,
    };

    try {
      if (globalState.selectionData.coveredProduction > 0) {
        await localActions.calculatePremiums(null, errorCB, false);
      }

      const data = await NetBenefitEstimatorService.getEstimatedData(params);

      setEstimatedData(data);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      errorCB(error);
    }
  };

  useEffect(() => {
    fetchEstimatedData();
  }, [
    globalState.selectionData.selectedYear,
    globalState.selectionData.coveredProduction,
    globalState.selectionData.coverageLevel,
    globalState.selectionData.tier2CoverageLevel,
    globalState.selectionData.coveragePercentage,
  ]);

  return (
    <DefaultLayout>
      {loader && (<Loader showLoader={loader} type="refresh" />)}
      <Navigation selectedIndex={1} />
      <div className="net-benefit-estimator-container">

        <SelectionInputs />

        {
          estimatedData && estimatedData.benefitEstimates && !!estimatedData.benefitEstimates.length
            && (
              <div className="table-container">
                <Table sx={{ borderColor: NEUTRAL }}>
                  <Thead bg={BACKGROUND_TABLE_HEADER}>
                    <Tr>
                      <Th textAlign="end">{t('netBenefitEstimator.tableHeaders.month').toUpperCase()}</Th>
                      <Th textAlign="end">{t('netBenefitEstimator.tableHeaders.forcastedMargin').toUpperCase()}</Th>
                      <Th textAlign="end">{t('netBenefitEstimator.tableHeaders.t1ProdHistory').toUpperCase()}</Th>
                      <Th textAlign="end">{t('netBenefitEstimator.tableHeaders.t2ProdHistory').toUpperCase()}</Th>
                      <Th textAlign="end">{t('netBenefitEstimator.tableHeaders.t1CoveredPayment').toUpperCase()}</Th>
                      <Th textAlign="end">{t('netBenefitEstimator.tableHeaders.t2CoveredPayment').toUpperCase()}</Th>
                      <Th textAlign="end">{t('netBenefitEstimator.tableHeaders.totalPayment').toUpperCase()}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {
                      estimatedData.benefitEstimates.map(tableRow => (
                        <Tr key={tableRow.month} _hover={{ bg: BACKGROUND_HOVER }}>
                          <Td isNumeric fontWeight="semibold">{getMonthName(tableRow.month)}</Td>
                          <Td isNumeric>{formatterCurrency.format(tableRow.margin)}</Td>
                          <Td isNumeric>{formatterWithoutCurrency.format(tableRow.tier1CoveredProduction)}</Td>
                          <Td isNumeric>{formatterWithoutCurrency.format(tableRow.tier2CoveredProduction)}</Td>
                          <Td isNumeric>{formatterCurrency.format(tableRow.tier1CoveredPaymentPerCwt)}</Td>
                          <Td isNumeric>{formatterCurrency.format(tableRow.tier2CoveredPaymentPerCwt)}</Td>
                          <Td isNumeric>{formatterCurrency.format(tableRow.total)}</Td>
                        </Tr>
                      ))
                    }
                  </Tbody>
                  {
                    estimatedData.benefitEstimates
                  && (
                    <Tfoot>
                      <Tr bgColor="gray.100">
                        <Td isNumeric>{globalState.selectionData.selectedYear}</Td>
                        <Td isNumeric>{formatterCurrency.format(estimatedData.benefitEstimates.reduce((a, b) => a + b.margin, 0) / estimatedData.benefitEstimates.length)}</Td>
                        <Td isNumeric>
                          {formatterWithoutCurrency.format(estimatedData.benefitEstimates.reduce((a, b) => a + b.tier1CoveredProduction, 0))}
                        </Td>
                        <Td isNumeric>
                          {formatterWithoutCurrency.format(estimatedData.benefitEstimates.reduce((a, b) => a + b.tier2CoveredProduction, 0))}
                        </Td>
                        <Td isNumeric>
                          {formatterCurrency.format(estimatedData.benefitEstimates.reduce((a, b) => a + b.tier1CoveredPaymentPerCwt, 0) / estimatedData.benefitEstimates.length)}
                        </Td>
                        <Td isNumeric>
                          {formatterCurrency.format(estimatedData.benefitEstimates.reduce((a, b) => a + b.tier2CoveredPaymentPerCwt, 0) / estimatedData.benefitEstimates.length)}
                        </Td>
                        <Td isNumeric>{formatterCurrency.format(estimatedData.benefitEstimates.reduce((a, b) => a + b.total, 0))}</Td>
                      </Tr>
                    </Tfoot>
                  )
                  }
                </Table>
              </div>
            )
        }
      </div>

    </DefaultLayout>
  );
};

export default NetBenefitEstimator;
